import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { useAppSelector } from "@/store/hooks";
import type { PhonicsEncodeSlideData } from "@ai-teach/types";
import { useRef, useState } from "react";
import { HiVolumeUp } from "react-icons/hi";
import { IoCheckmarkCircleOutline, IoCloseCircleOutline, IoPause } from "react-icons/io5";

export function PhonicsEncodeLiveMobileView() {
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);

  if (!slide) return null;

  const data = slide.slideData as PhonicsEncodeSlideData | undefined;
  const mode = data?.questionMode ?? "multiple_choice";
  const correctAnswer = data?.correctAnswer;
  const options = data?.options ?? [];
  const audioSrc = data?.audio?.src;

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const allOptions = correctAnswer ? [correctAnswer, ...options] : options;
  const norm = (s: string) => s.trim().toLowerCase();

  return (
    <LiveMobileViewContainer>
      {/* Hero card — full-bleed, square, content centered; matches PhonicsEncode.tsx heroCard. */}
      <div
        className="flex min-h-[320px] w-full shrink-0 flex-col items-center justify-center gap-8 bg-white px-6 py-10"
        style={{ boxShadow: "0 6px 15px rgba(0,0,0,0.12)" }}
      >
        <div className="rounded-[15px] px-4 py-1.5" style={{ backgroundColor: "#FFE89F" }}>
          <span className="text-xl font-bold" style={{ color: "#727171" }}>
            Listen closely
          </span>
        </div>

        <p className="text-center text-xl font-bold" style={{ color: "#2626A5" }}>
          {mode === "multiple_choice"
            ? "Which spelling matches the sound?"
            : "Type the spelling for this sound"}
        </p>

        {audioSrc && (
          // biome-ignore lint/a11y/useMediaCaption: phonics audio is pronunciation teaching
          <audio ref={audioRef} src={audioSrc} onEnded={() => setPlaying(false)} />
        )}
        <button
          type="button"
          onClick={toggleAudio}
          disabled={!audioSrc}
          className="flex h-24 w-24 items-center justify-center rounded-full shadow-lg transition-opacity disabled:opacity-40"
          style={{ backgroundColor: "#FFAC27" }}
        >
          {playing ? (
            <IoPause className="text-white" style={{ fontSize: 44 }} />
          ) : (
            <HiVolumeUp className="text-white" style={{ fontSize: 42 }} />
          )}
        </button>
      </div>

      {/* Answer area */}
      {mode === "multiple_choice" ? (
        <div className="flex w-full min-h-0 flex-1 flex-col items-center gap-4 overflow-y-auto px-4 pt-4 pb-4">
          {allOptions.map((opt, i) => {
            const isCorrectOpt = !!correctAnswer && norm(opt) === norm(correctAnswer);
            const isSuccess = !!chosen && isCorrectOpt;
            const isFail = chosen === opt && !isCorrectOpt;
            const isDimmed = !!chosen && !isSuccess && !isFail;
            return (
              <button
                key={`preview-opt-${i + 1}`}
                type="button"
                disabled={!!chosen}
                onClick={() => setChosen(opt)}
                //原來樣式
                //flex min-h-[49px] w-[322px] max-w-full items-center justify-between rounded-2xl border-2 px-6 py-2.5 text-xl font-bold
                className={`
                    relative flex min-h-[49px] w-[322px] max-w-full items-center justify-center rounded-xl border-2 px-6 py-2.5
                  ${isDimmed ? "opacity-50" : ""}`}
                style={{
                  borderColor: isSuccess ? "#34C759" : isFail ? "#FF4C39" : "#DDDDDD",
                  backgroundColor: isSuccess ? "#9DF0B2" : isFail ? "#FFA49A" : "#FFFFFF",
                  color: isFail ? "#FF4C39" : "#000000",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                }}
              >
                <span>{opt || `Option ${i + 1}`}</span>
                {isSuccess && (
                  <IoCheckmarkCircleOutline
                    //避免壓到文字空間，固定在右邊
                    className="absolute right-6"
                    size={22}
                    color="#000000"
                  />
                )}
                {isFail && (
                  <IoCloseCircleOutline className="absolute right-6" size={22} color="#000000" />
                )}
              </button>
            );
          })}
          {allOptions.length === 0 && (
            <div className="w-[322px] max-w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 py-2 text-center text-xs italic text-gray-400">
              Add options above
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className="mt-4 flex min-h-[49px] w-[322px] max-w-full items-center self-center rounded-2xl border-2 bg-white px-6 py-2.5 text-xl italic text-gray-400"
            style={{
              borderColor: "#DDDDDD",
              boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
            }}
          >
            Type your answer here...
          </div>
          <div className="flex-1" />
        </>
      )}
    </LiveMobileViewContainer>
  );
}
